import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { useContext } from 'react';
import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { ApprovalButton } from 'ui-product-approvals/components/button/ApprovalButton';
import { ApprovalEditorButtons } from 'ui-product-approvals/components/consumer/ApprovalEditorButtons';
import { useApprovalConditions } from 'ui-product-approvals/hooks/useApprovalConditions';
import { useHasV2ApprovalEditorButtons } from 'ui-product-approvals/hooks/useHasV2ApprovalEditorButtons';
import { useCollaborationSidebarOpenState } from 'collaboration-sidebar/public/hooks/useCollaborationSidebarOpenState';
import { useCollaborationSidebarHasUnrecoverableError } from 'collaboration-sidebar/public/hooks/useCollaborationSidebarHasUnrecoverableError';
import { EditorApprovalContext } from './EditorApprovalProvider';
import useGetIsApprovalsEnabled from './useGetIsApprovalsEnabled';
import ApprovalAvailabilityTooltip from 'ContentEditorUI/components/approvals/ApprovalAvailabilityTooltip';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getNumErrorsAndWarnings = ({
  errorsAndWarnings,
  errorsAndWarningsAndInfosCount
}) => {
  if (errorsAndWarningsAndInfosCount) {
    return errorsAndWarningsAndInfosCount;
  }
  if (errorsAndWarnings) {
    return errorsAndWarnings.length;
  }
  return 0;
};

// Borrowed from foundations-components/transitional/components/Button/Button.helpers.ts
const getButtonTokens = buttonSize => {
  switch (buttonSize) {
    case 'md':
    case 'medium':
      return {
        iconButtonSize: 'small',
        paddingBlock: '300',
        maxBlockSize: '40px'
      };
    case 'xs':
    case 'extra-small':
      return {
        iconButtonSize: 'extra-small',
        paddingBlock: '100',
        maxBlockSize: '24px'
      };
    case 'sm':
    case 'small':
    default:
      return {
        iconButtonSize: 'extra-small',
        paddingBlock: '200',
        maxBlockSize: '32px'
      };
  }
};
const ButtonSkeleton = styled.div.withConfig({
  displayName: "EditorApprovalButtons__ButtonSkeleton"
})(["background-color:", ";border-radius:", ";padding:", ";display:inline-flex;min-inline-size:74px;block-size:32px;flex-grow:1;align-items:center;justify-content:center;user-select:none;cursor:pointer;max-width:100%;overflow:hidden;box-sizing:border-box;"], ({
  theme
}) => theme.color['fill-primary-default'], ({
  theme
}) => theme.borderRadius['200'], ({
  theme
}) => theme.space['300']);
const ButtonSkeletonText = styled.div.withConfig({
  displayName: "EditorApprovalButtons__ButtonSkeletonText"
})(["border-radius:", ";background:", ";width:", ";height:12px;opacity:0.5;"], ({
  theme
}) => theme.borderRadius['200'], ({
  theme
}) => theme.color['border-core-subtle'], ({
  $width
}) => $width);
const DropdownIconButton = buttonProps => /*#__PURE__*/_jsx(IconButton, Object.assign({}, buttonProps, {
  children: /*#__PURE__*/_jsx(DownIcon, {})
}));
const EditorApprovalButtons = ({
  children,
  onOpenWarningsAndSuggestionsPanel,
  errorsAndWarnings,
  isTestVariation = false,
  testVariationDisabledTooltip,
  dropdownProps,
  extraButtons,
  buttonSize,
  hasWriteAccessToProductApprovals,
  extraActionButtons,
  errorsAndWarningsAndInfosCount
}) => {
  const {
    openComments,
    openApprovals
  } = useCollaborationSidebarOpenState();
  const {
    hasUnrecoverableError
  } = useCollaborationSidebarHasUnrecoverableError();
  const {
    isFetchComplete,
    isPending,
    isCancelled,
    isApprover,
    isUserStepApproved,
    isNonParticipant,
    canUserTakeApprovableAction,
    isRequester
  } = useApprovalConditions();
  const isApprovalPendingOrCancelled = isPending || isCancelled;
  if (!isFetchComplete) {
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ButtonSkeleton, {
        "data-test-id": "loading-approval",
        children: /*#__PURE__*/_jsx(ButtonSkeletonText, {
          $width: "40px"
        })
      })
    );
  }
  if (canUserTakeApprovableAction) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  const numErrorsAndWarningsAndInfosCount = getNumErrorsAndWarnings({
    errorsAndWarnings,
    errorsAndWarningsAndInfosCount
  });
  const requestChangesButton = /*#__PURE__*/_jsx(ApprovalAvailabilityTooltip, {
    children: /*#__PURE__*/_jsx(Button, {
      "data-test-id": "editor-approval-request-changes-button",
      disabled: hasUnrecoverableError,
      onClick: () => openComments(),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "approvals.requestChanges"
      })
    }, 2)
  });
  const approvalButton = isTestVariation ? /*#__PURE__*/_jsx(Tooltip, {
    title: testVariationDisabledTooltip,
    placement: "left",
    children: /*#__PURE__*/_jsx(ApprovalButton, {
      "data-test-id": "editor-approval-button",
      disabled: true,
      size: buttonSize
    }, 1)
  }) : /*#__PURE__*/_jsx(ApprovalButton, {
    "data-test-id": "editor-approval-button",
    disabled: !hasWriteAccessToProductApprovals,
    size: buttonSize
  }, 1);
  const viewApprovalButton = /*#__PURE__*/_jsx(ApprovalAvailabilityTooltip, {
    children: /*#__PURE__*/_jsx(Button, {
      "data-test-id": "editor-approval-view-approval-button",
      disabled: hasUnrecoverableError,
      onClick: () => openApprovals(),
      use: "primary",
      size: buttonSize,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "approvals.viewApprovalActivity"
      })
    }, 3)
  });
  const warningsButton = /*#__PURE__*/_jsxs(Button, {
    "data-test-id": "editor-approval-warnings-button",
    onClick: onOpenWarningsAndSuggestionsPanel,
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "approvals.warningsAndSuggestions"
    }), numErrorsAndWarningsAndInfosCount > 0 && /*#__PURE__*/_jsx(Badge, {
      use: "count",
      children: numErrorsAndWarningsAndInfosCount
    })]
  }, 4);
  const shouldShowViewApprovalButtonAsMain = isPending && (isNonParticipant || isRequester || isUserStepApproved);
  const mainButton = shouldShowViewApprovalButtonAsMain ? viewApprovalButton : approvalButton;
  const primaryButtons = [warningsButton];
  if (isApprovalPendingOrCancelled && !shouldShowViewApprovalButtonAsMain) {
    primaryButtons.unshift(viewApprovalButton);
  }
  if (isRequester && isPending) {
    primaryButtons.unshift(approvalButton);
  }
  if (isApprover) {
    primaryButtons.unshift(requestChangesButton);
  }
  const {
    iconButtonSize,
    maxBlockSize,
    paddingBlock
  } = getButtonTokens(buttonSize);
  return /*#__PURE__*/_jsxs(ButtonGroup, {
    display: "flex",
    children: [mainButton, /*#__PURE__*/_jsx(Dropdown, Object.assign({
      "data-test-id": "editor-approval-buttons-dropdown",
      Button: DropdownIconButton,
      buttonSize: iconButtonSize,
      buttonStyles: {
        borderInlineStartColor: 'fill-accent-orange-hover',
        borderInlineStartWidth: '100',
        borderInlineStartStyle: 'solid',
        paddingBlock,
        paddingInline: '100',
        // HACK: Remove once https://git.hubteam.com/HubSpot/foundations-components/issues/5632 is addressed
        maxBlockSize
      },
      buttonUse: "primary",
      caretRenderer: null
    }, dropdownProps, {
      children: /*#__PURE__*/_jsxs(List, {
        children: [extraActionButtons ? extraActionButtons.map(({
          label,
          onClick,
          id
        }, index) => /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Button, {
            "data-test-id": id,
            onClick: onClick,
            children: label
          })
        }, index)) : extraButtons, primaryButtons.map((button, index) => /*#__PURE__*/_jsx(ListItem, {
          children: button
        }, index))]
      })
    }))]
  });
};
const EditorApprovalButtonsV2 = ({
  children,
  isTestVariation,
  testVariationDisabledTooltip,
  onOpenWarningsAndSuggestionsPanel,
  errorsAndWarnings,
  errorsAndWarningsAndInfosCount,
  extraActionButtons,
  dropdownProps,
  buttonSize
}) => {
  const numCount = getNumErrorsAndWarnings({
    errorsAndWarnings,
    errorsAndWarningsAndInfosCount
  });
  const additionalDropdownButtons = [...(extraActionButtons ? extraActionButtons.map(({
    label,
    onClick,
    id
  }) => /*#__PURE__*/_jsx(Button, {
    "data-test-id": id,
    onClick: onClick,
    children: label
  }, id)) : []), /*#__PURE__*/_jsxs(Button, {
    "data-test-id": "editor-approval-warnings-button",
    onClick: onOpenWarningsAndSuggestionsPanel,
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "approvals.warningsAndSuggestions"
    }), numCount > 0 && /*#__PURE__*/_jsx(Badge, {
      use: "count",
      children: numCount
    })]
  }, "warnings")];
  return /*#__PURE__*/_jsx(ApprovalEditorButtons, {
    approvalOperationDisabledReason: isTestVariation ? testVariationDisabledTooltip !== null && testVariationDisabledTooltip !== void 0 ? testVariationDisabledTooltip : true : undefined,
    additionalDropdownButtons: additionalDropdownButtons,
    dropdownProps: dropdownProps,
    size: buttonSize,
    children: children
  });
};
const EditorApprovalButtonsWrapper = _ref => {
  let {
      children
    } = _ref,
    editorApprovalButtonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    hasReadAccessToProductApprovals,
    hasWriteAccessToProductApprovals
  } = useContext(EditorApprovalContext);
  const isApprovalsEnabled = useGetIsApprovalsEnabled();
  const hasV2 = useHasV2ApprovalEditorButtons();
  if (hasV2) {
    return /*#__PURE__*/_jsx(EditorApprovalButtonsV2, Object.assign({}, editorApprovalButtonProps, {
      children: children
    }));
  }
  if (!hasReadAccessToProductApprovals || isApprovalsEnabled === false) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(EditorApprovalButtons, Object.assign({}, editorApprovalButtonProps, {
    hasWriteAccessToProductApprovals: hasWriteAccessToProductApprovals,
    children: children
  }));
};
export default EditorApprovalButtonsWrapper;